import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Le TYPE d'un fichier, déduit de son extension.
 *
 * 🔴 DÉDUIT PLUTÔT QUE DEMANDÉ, ET C'EST CE QUI PERMET UN SEUL VISUALISEUR.
 * Une pièce jointe de reçu est un PDF **ou** une photo — l'appelant ne le sait
 * pas avant de regarder le chemin, et le lui faire décider aurait remis une
 * condition dans chacun des quatre sites.
 *
 * ⚠️ L'EXTENSION, PAS L'EN-TÊTE HTTP : le serveur rend bien un `Content-Type`,
 * mais il n'arrive qu'APRÈS la requête — or il faut choisir le rendu avant de
 * savoir quoi demander. *Une déduction faite trop tard oblige à un second
 * rendu, donc à un clignotement.*
 */
export const typeDuFichier = (chemin) => {
  const ext = String(chemin || '').split('.').pop().toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'avif'].includes(ext)) {
    return { mimeType: `image/${ext === 'jpg' ? 'jpeg' : ext}`, estImage: true };
  }
  return { mimeType: 'application/pdf', estImage: false };
};

/**
 * LE visualiseur — un seul pour tout ce que l'application donne à regarder.
 *
 * 🔴 IL NE SERVAIT QUE LE PDF DU BON DE COMMANDE, ET TROIS AUTRES SURFACES
 * OUVRAIENT UN ONGLET. Mesuré au lot C : la pièce jointe d'un reçu et la photo
 * de facture d'un paiement partaient en `href` + `target="_blank"`, et l'image
 * du produit n'était pas cliquable du tout. **Trois façons de regarder un
 * fichier dans la même application.**
 *
 * ⚠️ ET L'ONGLET N'EST PAS QU'INCONFORTABLE : il **PERD L'AUTHENTIFICATION
 * UTILE**. Le jeton voyage par cookie, donc le fichier s'affiche — mais
 * `X-Stock-ID` est posé par un INTERCEPTEUR axios, que le navigateur n'applique
 * pas à une navigation. *Un chemin qui contourne le client d'API contourne
 * aussi tout ce que le client d'API garantit.* Ici, tout passe par `api.get`.
 *
 * ⚠️ LE BLOB EST RÉVOQUÉ AU DÉMONTAGE (`URL.revokeObjectURL`) : sans ça, chaque
 * ouverture retiendrait le fichier entier en mémoire jusqu'au rechargement de
 * la page. Sur des photos de facture, c'est quelques mégaoctets par regard.
 */
const DocumentViewerModal = ({
  open, onClose, url, title, filename, mimeType = 'application/pdf',
  estImage = false, actions = null,
}) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (!open || !url) return undefined;

    let createdUrl = null;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await api.get(url, { responseType: 'blob' });
        if (cancelled) return;
        const blob = new Blob([response.data], { type: mimeType });
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      } catch (err) {
        console.error('Erreur lors du chargement du document:', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      setObjectUrl(null);
    };
  }, [open, url, mimeType]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" component="span">{title}</Typography>
        <IconButton onClick={onClose} aria-label="close" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: '80vh' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{t('common.error')}</Alert>
          </Box>
        )}
        {/* 🔴 UNE IMAGE DANS UN `<img>`, PAS DANS UNE `<iframe>`. Le navigateur
            SAIT afficher une image dans une iframe — mais collée en haut à
            gauche, à sa taille native, avec ses propres barres de défilement.
            `objectFit: contain` la fait tenir dans le cadre en gardant ses
            proportions. *Ce qui « marche » et ce qui se regarde ne sont pas la
            même chose.* */}
        {!loading && !error && objectUrl && (
          estImage ? (
            <Box sx={{
              width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'action.hover', p: 1,
            }}>
              <Box component="img" src={objectUrl} alt={title || filename || ''}
                   sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </Box>
          ) : (
            <iframe
              src={objectUrl}
              title={title || filename || 'document'}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )
        )}
      </DialogContent>
      {/* ⚠️ LES ACTIONS SONT FOURNIES PAR L'APPELANT, jamais construites ici :
          remplacer une pièce jointe n'a de sens que pour un reçu. Le
          visualiseur reste ignorant de ce qu'il montre — c'est ce qui lui
          permet de tout montrer. */}
      {actions ? <DialogActions>{actions}</DialogActions> : null}
    </Dialog>
  );
};

export default DocumentViewerModal;
